import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "qrcode";
import { kioskSupabase as supabase } from "../../lib/supabase";
import { canvasToCameraJpeg } from "../../utils/facialImage";

type Gym = {
  id: string;
  nombre: string;
  slug: string;
  logo_url?: string | null;
  color_primario?: string;
  color_secundario?: string;
};
type Branch = { id: string; nombre: string; direccion?: string | null };
type Plan = {
  id: string;
  nombre: string;
  descripcion?: string | null;
  precio: number;
  matricula?: number;
  permanencia_meses?: number | null;
  duracion_dias: number;
  beneficios?: string[];
  modalidad_cobro: "unico" | "recurrente";
  proveedor_recurrente: string | null;
  proveedores_recurrentes?: string[] | null;
  permitir_webpay_plus_kiosco?: boolean;
  max_dias_semana?: number | null;
  max_sesiones_periodo?: number | null;
};
const recurringProviders = (plan: Plan | null) => {
  if (!plan) return [];
  const providers = Array.isArray(plan.proveedores_recurrentes)
    ? plan.proveedores_recurrentes.filter(Boolean)
    : [];
  return providers.length ? providers : (plan.proveedor_recurrente ? [plan.proveedor_recurrente] : []);
};
const recurringProviderName = (provider: string) => ({
  mercadopago: "Mercado Pago",
  flow: "Flow",
  fintoc: "Fintoc",
  transbank_oneclick_mall: "Transbank Oneclick",
}[provider] || provider);
type ContractDocument = {
  id: string;
  codigo: string;
  titulo: string;
  version: number;
  contenido: string;
  obligatorio: boolean;
};
type Catalog = {
  gimnasio: Gym;
  sucursales: Branch[];
  planes: Plan[];
  documentos: ContractDocument[];
};

const money = (value: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(value);
const phone = (value: string) => {
  const digits = value.replace(/\D/g, "");
  const local = digits.startsWith("56") ? digits.slice(2) : digits;
  return `+56${local}`;
};
const validPhone = (value: string) => /^\+569\d{8}$/.test(phone(value));
const validEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const normalizeRut = (value: string) =>
  value.toUpperCase().replace(/[^0-9K]/g, "");
const validRut = (value: string) => {
  const rut = normalizeRut(value);
  if (!/^\d{7,8}[0-9K]$/.test(rut)) return false;
  const body = rut.slice(0, -1),
    dv = rut.slice(-1);
  let sum = 0,
    m = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += Number(body[i]) * m;
    m = m === 7 ? 2 : m + 1;
  }
  const r = 11 - (sum % 11);
  return (r === 11 ? "0" : r === 10 ? "K" : String(r)) === dv;
};
const publicError = (error: unknown) => {
  const original = error instanceof Error ? error.message : String(error || "");
  const message = original.toLowerCase();
  if (
    message.includes("documentos fueron actualizados") ||
    message.includes("revisa los datos y aceptaciones")
  )
    return original;
  if (
    message.includes("already registered") ||
    message.includes("already been registered") ||
    message.includes("user already exists")
  )
    return "Ya existe una cuenta asociada a este correo. Puedes iniciar sesión o recuperar tu contraseña.";
  if (
    message.includes("invalid login") ||
    message.includes("invalid credentials")
  )
    return "La cuenta ya existe, pero la contraseña ingresada no coincide. Usa tu contraseña anterior o solicita recuperación de acceso.";
  if (message.includes("password"))
    return "La contraseña no cumple los requisitos de seguridad. Usa al menos 8 caracteres.";
  if (message.includes("rate limit") || message.includes("too many"))
    return "Se realizaron demasiados intentos. Espera unos minutos antes de volver a intentar.";
  if (message.includes("network") || message.includes("fetch"))
    return "No pudimos conectarnos. Revisa la conexión a internet e intenta nuevamente.";
  if (message.includes("database error") || message.includes("saving new user"))
    return "No pudimos completar el registro. Revisa tus datos o utiliza otro correo si ya tienes una cuenta.";
  return "No pudimos completar el registro en este momento. Intenta nuevamente o solicita ayuda en recepción.";
};

export default function InscripcionKioscoPage() {
  const { gimnasioSlug } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState("");
  const [enabledRecurringProviders, setEnabledRecurringProviders] = useState<string[]>([]);
  const [webpayEnabledForBranch, setWebpayEnabledForBranch] = useState(false);
  const [permanenciaMeses, setPermanenciaMeses] = useState<6 | 12>(12);
  const [branchId, setBranchId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [paymentStarting, setPaymentStarting] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentQr, setPaymentQr] = useState("");
  const [paymentQrUrl, setPaymentQrUrl] = useState("");
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const reconciliationStarted = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStream = useRef<MediaStream | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [facialConsent, setFacialConsent] = useState(false);
  const [facialSaving, setFacialSaving] = useState(false);
  const [facialComplete, setFacialComplete] = useState(false);
  const [openDocument, setOpenDocument] = useState<ContractDocument | null>(
    null,
  );
  const [form, setForm] = useState({
    nombre: "",
    rut: "",
    email: "",
    telefono: "",
    fechaNacimiento: "",
    password: "",
    confirmacion: "",
    mismoResponsable: true,
    responsableNombre: "",
    responsableRut: "",
    responsableEmail: "",
    responsableTelefono: "",
    saludEstado: "sin_condicion",
    saludDetalle: "",
    saludRestricciones: "",
    emergenciaNombre: "",
    emergenciaTelefono: "",
    firmaNombre: "",
    firmaRut: "",
    datosFirmaConfirmados: false,
    documentos: {} as Record<string, boolean>,
    aceptaciones: {
      contrato: false,
      reglamento: false,
      condiciones: false,
      salud_datos: false,
      pago_recurrente: false,
      retiro_anticipado: false,
      veracidad_salud: false,
    } as Record<string, boolean>,
  });
  const accent = useMemo(
    () => catalog?.gimnasio.color_primario || "#0f766e",
    [catalog],
  );
  const activeRecurringProviders = (plan: Plan | null) =>
    recurringProviders(plan).filter((provider) => enabledRecurringProviders.includes(provider));

  useEffect(() => {
    let active = true;
    const loadBranchPaymentMethods = async () => {
      setEnabledRecurringProviders([]);
      setWebpayEnabledForBranch(false);
      if (!branchId || !gimnasioSlug) return;
      const { data, error: paymentMethodsError } = await supabase.rpc("kiosco_metodos_pago_sucursal", {
        p_gimnasio_slug: gimnasioSlug,
        p_sucursal_id: branchId,
      });
      if (!active) return;
      if (paymentMethodsError) {
        setPaymentError("No pudimos cargar los medios de pago habilitados para esta sucursal.");
        return;
      }
      const methods = data as { proveedores_recurrentes?: string[]; webpay_plus?: boolean } | null;
      const providers = Array.isArray(methods?.proveedores_recurrentes) ? methods.proveedores_recurrentes : [];
      setEnabledRecurringProviders(providers);
      setWebpayEnabledForBranch(methods?.webpay_plus === true);
      setSelectedPaymentProvider((current) => providers.includes(current) ? current : "");
    };
    void loadBranchPaymentMethods();
    return () => { active = false; };
  }, [branchId, gimnasioSlug]);

  // La captura puede realizarla recepción desde otro equipo mientras esta
  // pantalla sigue abierta. Reconciliamos con el servidor para no solicitar
  // nuevamente una fotografía que ya está enrolada.
  useEffect(() => {
    if (!paymentComplete || facialComplete) return;
    let active = true;
    const reconcileExistingFace = async () => {
      const { data: auth } = await supabase.auth.getUser();
      const authId = auth.user?.id;
      if (!authId) return;
      const { data: user } = await supabase
        .from("usuarios")
        .select("id")
        .eq("auth_id", authId)
        .maybeSingle();
      if (!user?.id) return;
      const { data: biometric } = await supabase
        .from("usuarios_biometria_facial")
        .select("id,estado")
        .eq("usuario_id", user.id)
        .eq("estado", "enrolado")
        .maybeSingle();
      if (active && biometric?.id) setFacialComplete(true);
    };
    void reconcileExistingFace();
    const timer = window.setInterval(() => void reconcileExistingFace(), 3000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [paymentComplete, facialComplete]);
  const allDocumentsAccepted =
    !!catalog?.documentos.length &&
    catalog.documentos.every((document) => !!form.documentos[document.id]);
  const allSpecificAcceptances = Object.values(form.aceptaciones).every(
    Boolean,
  );

  // `load` is intentionally re-created from the current route slug.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!gimnasioSlug) {
      navigate("/inscripcion/qr", { replace: true });
      return;
    }
    void load();
  }, [gimnasioSlug, navigate]);
  const load = async () => {
    setLoading(true);
    setError("");
    setCatalog(null);
    setSelectedPlan(null);
    try {
      if (gimnasioSlug !== "mowe")
        throw new Error(
          "Esta autoinscripción está disponible solamente para Mowe.",
        );
      const { data, error: e } = await supabase.rpc("kiosco_catalogo", {
        p_gimnasio_slug: gimnasioSlug,
      });
      if (e) throw e;
      if (!data)
        throw new Error(
          "Este gimnasio no está disponible para autoinscripción.",
        );
      const next = data as Catalog;
      setCatalog(next);
      const storedPlan = sessionStorage.getItem("kiosco_plan_id");
      if (storedPlan) {
        const restored =
          next.planes.find((plan) => plan.id === storedPlan) || null;
        setSelectedPlan(restored);
        setSelectedPaymentProvider(recurringProviders(restored)[0] || "");
        const storedPermanencia = Number(
          sessionStorage.getItem("kiosco_permanencia_meses"),
        );
        setPermanenciaMeses(
          storedPermanencia === 6 || storedPermanencia === 12
            ? storedPermanencia
            : restored?.permanencia_meses === 6
              ? 6
              : 12,
        );
      }
      if (next.sucursales.length === 1) setBranchId(next.sucursales[0].id);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "No se pudo cargar la autoinscripción.",
      );
    } finally {
      setLoading(false);
    }
  };

  const returnedPreapproval = useMemo(() => {
    const params = new URLSearchParams(
      window.location.search.replace("?preapproval_id=", "&preapproval_id="),
    );
    return (
      params.get("preapproval_id") ||
      window.location.href.match(/[?&]preapproval_id=([^&#]+)/)?.[1] ||
      ""
    );
  }, []);
  const returnedWebpayToken = useMemo(
    () => new URLSearchParams(window.location.search).get("token_ws") || "",
    [],
  );
  const returnedFintoc = useMemo(
    () => new URLSearchParams(window.location.search).get("fintoc_id") || "",
    [],
  );
  const returnedFlow = useMemo(() => new URLSearchParams(window.location.search).get("flow_subscription_id") || "", []);
  const returnedFlowError = useMemo(() => new URLSearchParams(window.location.search).get("flow_error") || "", []);
  const [flowPending, setFlowPending] = useState(false);
  const reconcileFlow = async () => {
    setDone(true); setReconciling(true); setPaymentError("");
    try {
      const {data,error:e}=await supabase.functions.invoke("flow-subscription-start",{body:{action:"reconcile",subscription_id:returnedFlow}});
      if(e)throw e;
      if(data?.sucursal_id)setBranchId(String(data.sucursal_id));
      if(data?.plan_id){const returnedPlan=catalog?.planes.find((plan)=>plan.id===String(data.plan_id));if(returnedPlan)setSelectedPlan(returnedPlan)}
      if(data?.activated){setPaymentComplete(true);setFlowPending(false);window.history.replaceState({},"",window.location.pathname)}
      else if(data?.requires_retry){setFlowPending(false);setPaymentError("Flow no confirmó el registro de la tarjeta y no se realizó ningún cobro. Puedes volver a inscribirla con Flow.");window.history.replaceState({},"",window.location.pathname)}
      else{setFlowPending(true);setPaymentError(String(data?.message||"Flow está procesando la primera mensualidad. Puedes comprobarla nuevamente."))}
    }catch{setFlowPending(true);setPaymentError("Tu tarjeta puede estar registrada aunque Flow todavía no confirme el cobro. No vuelvas a inscribirla; utiliza este botón para actualizar el estado.")}finally{setReconciling(false)}
  };
  useEffect(()=>{if(!catalog||!returnedFlow||reconciliationStarted.current)return;reconciliationStarted.current=true;void reconcileFlow()},[catalog,returnedFlow]); // eslint-disable-line react-hooks/exhaustive-deps
  const [fintocPending, setFintocPending] = useState(false);
  const reconcileFintoc = async () => {
    setDone(true);
    setReconciling(true);
    setPaymentError("");
    try {
      const { data, error: e } = await supabase.functions.invoke(
        "fintoc-subscription-start",
        { body: { action: "reconcile", inscripcion_id: returnedFintoc } },
      );
      if (e) throw e;
      if (data?.activated) {
        setPaymentComplete(true);
        setFintocPending(false);
        window.history.replaceState({}, "", window.location.pathname);
      } else if (data?.estado === "expired") {
        setFintocPending(false);
        setPaymentError(
          "La inscripción Fintoc expiró. Puedes volver a intentarlo.",
        );
      } else {
        setFintocPending(true);
        setPaymentError(
          "Fintoc aún no confirma el cobro. Tu inscripción quedó guardada; puedes comprobarlo nuevamente.",
        );
      }
    } catch {
      setFintocPending(true);
      setPaymentError("No pudimos comprobar el pago. Intenta nuevamente.");
    } finally {
      setReconciling(false);
    }
  };
  // Reconciliation must run once for the returned Fintoc checkout id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!catalog || !returnedFintoc || reconciliationStarted.current) return;
    reconciliationStarted.current = true;
    void reconcileFintoc();
  }, [catalog, returnedFintoc]);
  const returnedWebpayCanceled = useMemo(
    () => new URLSearchParams(window.location.search).get("canceled") === "1",
    [],
  );
  const reconcilePayment = async () => {
    if (!returnedPreapproval) return;
    setDone(true);
    setReconciling(true);
    setPaymentError("");
    try {
      let {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        session = (await supabase.auth.refreshSession()).data.session;
      }
      if (!session?.access_token)
        throw new Error(
          "La sesión de inscripción expiró. Solicita ayuda en recepción para confirmar tu pago.",
        );
      const { data, error: e } = await supabase.functions.invoke(
        "mercadopago-subscription-start",
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: {
            action: "reconcile",
            preapproval_id: returnedPreapproval,
            session_token: session.access_token,
          },
        },
      );
      if (e) {
        const context = (e as { context?: Response })?.context;
        if (context)
          try {
            const body = await context.clone().json();
            throw new Error(
              String(
                body?.error ||
                  "No pudimos confirmar el pago. Intenta nuevamente.",
              ),
            );
          } catch (parsed) {
            if (parsed instanceof Error) throw parsed;
          }
        throw e;
      }
      if (!data?.activated)
        throw new Error(
          String(
            data?.error ||
              "Mercado Pago todavía está confirmando el pago. Intenta nuevamente.",
          ),
        );
      setPaymentComplete(true);
      window.history.replaceState({}, "", window.location.pathname);
    } catch (e) {
      setPaymentError(
        e instanceof Error
          ? e.message
          : "No pudimos confirmar el pago. Intenta nuevamente.",
      );
    } finally {
      setReconciling(false);
    }
  };
  // Reconciliation must run once for the returned preapproval id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!catalog || !returnedPreapproval || reconciliationStarted.current)
      return;
    reconciliationStarted.current = true;
    void reconcilePayment();
  }, [catalog, returnedPreapproval]);
  const reconcileWebpay = async () => {
    if (!returnedWebpayToken) return;
    setDone(true);
    setReconciling(true);
    setPaymentError("");
    try {
      let {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        session = (await supabase.auth.refreshSession()).data.session;
      }
      if (!session?.access_token)
        throw new Error(
          "La sesión expiró. Solicita ayuda en recepción para confirmar el pago.",
        );
      const { data, error: e } = await supabase.functions.invoke(
        "payments-confirm",
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: { token_ws: returnedWebpayToken },
        },
      );
      if (e) throw e;
      if (!data?.ok || data?.status !== "authorized")
        throw new Error(
          data?.status === "rejected"
            ? "Transbank rechazó el pago. Puedes intentar con otra tarjeta."
            : "Transbank todavía está confirmando el pago. Intenta nuevamente.",
        );
      setPaymentComplete(true);
      window.history.replaceState({}, "", window.location.pathname);
    } catch (e) {
      setPaymentError(publicError(e));
    } finally {
      setReconciling(false);
    }
  };
  useEffect(() => {
    if (!catalog || !returnedWebpayToken || reconciliationStarted.current)
      return;
    reconciliationStarted.current = true;
    void reconcileWebpay();
  }, [catalog, returnedWebpayToken]);
  useEffect(() => {
    if (!catalog || !returnedWebpayCanceled || reconciliationStarted.current)
      return;
    reconciliationStarted.current = true;
    setDone(true);
    setPaymentError(
      "El pago fue cancelado. Puedes volver a intentarlo cuando quieras.",
    );
    window.history.replaceState({}, "", window.location.pathname);
  }, [catalog, returnedWebpayCanceled]);
  useEffect(
    () => () => {
      cameraStream.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );
  const openCamera = async () => {
    setPaymentError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 960 },
        },
        audio: false,
      });
      cameraStream.current?.getTracks().forEach((track) => track.stop());
      cameraStream.current = stream;
      setCameraOpen(true);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      });
    } catch {
      setPaymentError(
        "No pudimos abrir la cámara. Revisa el permiso de cámara del navegador e intenta nuevamente.",
      );
    }
  };
  const captureFace = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth)
      return setPaymentError(
        "La cámara todavía está iniciando. Espera un momento.",
      );
    if (!facialConsent)
      return setPaymentError(
        "Debes aceptar el consentimiento para registrar tu rostro.",
      );
    setFacialSaving(true);
    setPaymentError("");
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 480;
      canvas.height = 640;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("No se pudo preparar la captura facial.");
      const sourceRatio = video.videoWidth / video.videoHeight,
        targetRatio = canvas.width / canvas.height;
      let sx = 0,
        sy = 0,
        sw = video.videoWidth,
        sh = video.videoHeight;
      if (sourceRatio > targetRatio) {
        sw = video.videoHeight * targetRatio;
        sx = (video.videoWidth - sw) / 2;
      } else {
        sh = video.videoWidth / targetRatio;
        sy = (video.videoHeight - sh) / 2;
      }
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const consent = await supabase.rpc(
        "mobile_aceptar_consentimiento_biometrico",
        { p_version: "v1" },
      );
      if (consent.error) throw consent.error;
      const { data, error: e } = await supabase.functions.invoke(
        "usuario-facial-enroll",
        { body: { image_data_url: canvasToCameraJpeg(canvas) } },
      );
      if (e || !data?.ok)
        throw new Error(
          String(
            data?.details ||
              data?.error ||
              e?.message ||
              "No se pudo guardar la captura facial.",
          ),
        );
      cameraStream.current?.getTracks().forEach((track) => track.stop());
      cameraStream.current = null;
      setCameraOpen(false);
      setFacialComplete(true);
    } catch (e) {
      setPaymentError(
        e instanceof Error
          ? e.message
          : "No se pudo guardar la captura facial.",
      );
    } finally {
      setFacialSaving(false);
    }
  };

  const reset = async () => {
    await supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
    window.location.assign(window.location.pathname);
  };
  const startRecurringPayment = async (provider: string, showOnPhone = false) => {
    if (!selectedPlan) return;
    if (!recurringProviders(selectedPlan).includes(provider)) return;
    setSelectedPaymentProvider(provider);
    setPaymentStarting(true);
    setPaymentError("");
    try {
      const backUrl = `${window.location.origin}${window.location.pathname}`;
      let {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        const refreshed = await supabase.auth.refreshSession();
        session = refreshed.data.session;
      }
      if (!session?.access_token)
        throw new Error(
          "La sesión no está disponible. Vuelve a ingresar tu contraseña para continuar.",
        );
      const functionName = provider === "flow" ? "flow-subscription-start" : provider === "fintoc" ? "fintoc-subscription-start" : provider === "transbank_oneclick_mall" ? "oneclick-inscription-start" : "mercadopago-subscription-start";
      const { data, error: e } = await supabase.functions.invoke(
        functionName,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: {
            plan_id: selectedPlan.id,
            back_url: backUrl,
            session_token: session.access_token,
            provider,
          },
        },
      );
      if (e) throw e;
      if (data?.ok === false)
        throw new Error(
          String(data?.error || "No se pudo iniciar la suscripción"),
        );
      const initPoint = String(data?.init_point || "");
      if (!initPoint)
        throw new Error(
          String(data?.error || "No se recibió el enlace de inscripción"),
        );
      if (provider === "transbank_oneclick_mall" && data?.token) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = initPoint;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "TBK_TOKEN";
        input.value = String(data.token);
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
        return;
      }
      if (showOnPhone && ["mercadopago","flow"].includes(provider)) {
        setPaymentQrUrl(initPoint);
        setPaymentQr(await QRCode.toDataURL(initPoint, {
          width: 720, margin: 2, errorCorrectionLevel: "H",
          color: { dark: "#172111", light: "#ffffff" },
        }));
        setPaymentStarting(false);
      } else {
        window.location.assign(initPoint);
      }
    } catch (e) {
      let providerMessage = "";
      const context = (e as { context?: Response })?.context;
      if (context) {
        try {
          const body = await context.clone().json();
          const detail = body?.details;
          const detailMessage = String(
            detail?.message ||
              detail?.error ||
              detail?.cause?.[0]?.description ||
              "",
          ).trim();
          providerMessage = [String(body?.error || ""), detailMessage]
            .filter(Boolean)
            .join(": ");
          console.error("Mercado Pago:", body);
        } catch {
          /* respuesta no JSON */
        }
      }
      const known =
        providerMessage ||
        (e instanceof Error && e.message !== "registration failed"
          ? e.message
          : "");
      setPaymentError(known || publicError(e));
      setPaymentStarting(false);
    }
  };
  const startWebpay = async () => {
    if (!selectedPlan) return;
    setPaymentStarting(true);
    setPaymentError("");
    try {
      let {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        session = (await supabase.auth.refreshSession()).data.session;
      }
      if (!session?.access_token)
        throw new Error(
          "La sesión no está disponible. Vuelve a ingresar tu contraseña para continuar.",
        );
      const { data, error: e } = await supabase.functions.invoke(
        "payments-create-checkout",
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: {
            plan_id: selectedPlan.id,
            plan_nombre: selectedPlan.nombre,
            origen: "kiosco",
          },
        },
      );
      if (e) throw e;
      if (!data?.url || !data?.token)
        throw new Error("Transbank no entregó el enlace de pago.");
      const paymentForm = document.createElement("form");
      paymentForm.method = "POST";
      paymentForm.action = String(data.url);
      const token = document.createElement("input");
      token.type = "hidden";
      token.name = "token_ws";
      token.value = String(data.token);
      paymentForm.appendChild(token);
      document.body.appendChild(paymentForm);
      paymentForm.submit();
    } catch (e) {
      setPaymentError(publicError(e));
      setPaymentStarting(false);
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!catalog || !selectedPlan || !branchId)
      return setError("Selecciona sucursal y plan.");
    if (form.nombre.trim().length < 3)
      return setError("Ingresa tu nombre completo.");
    if (!validRut(form.rut)) return setError("Ingresa un RUT chileno válido.");
    if (!validEmail(form.email))
      return setError("Ingresa un correo electrónico válido.");
    if (!validPhone(form.telefono))
      return setError(
        "Ingresa un teléfono chileno válido de 9 dígitos. Ejemplo: 9 1234 5678.",
      );
    if (!form.fechaNacimiento)
      return setError("Selecciona tu fecha de nacimiento.");
    const birth = new Date(`${form.fechaNacimiento}T12:00:00`);
    if (Number.isNaN(birth.getTime()) || birth > new Date())
      return setError("La fecha de nacimiento no es válida.");
    if (
      !form.mismoResponsable &&
      (!validRut(form.responsableRut) ||
        form.responsableNombre.trim().length < 3 ||
        !validEmail(form.responsableEmail))
    )
      return setError(
        "Completa correctamente los datos del responsable de pago.",
      );
    if (
      form.saludEstado === "con_condicion" &&
      !form.saludDetalle.trim() &&
      !form.saludRestricciones.trim()
    )
      return setError(
        "Describe la condición o las restricciones relevantes para entrenar de forma segura.",
      );
    if (catalog.documentos.some((d) => d.obligatorio && !form.documentos[d.id]))
      return setError(
        "Debes revisar y aceptar todos los documentos contractuales.",
      );
    if (Object.values(form.aceptaciones).some((value) => !value))
      return setError("Debes confirmar todas las aceptaciones específicas.");
    if (
      form.firmaNombre.trim().toLocaleLowerCase("es-CL") !==
        form.nombre.trim().toLocaleLowerCase("es-CL") ||
      normalizeRut(form.firmaRut) !== normalizeRut(form.rut)
    )
      return setError(
        "Los datos de la firma deben coincidir con el nombre y RUT ingresados al inicio.",
      );
    if (!form.datosFirmaConfirmados)
      return setError(
        "Confirma que tus datos personales son correctos antes de firmar.",
      );
    if (form.password.length < 8)
      return setError("La contraseña debe tener al menos 8 caracteres.");
    if (form.password !== form.confirmacion)
      return setError("Las contraseñas no coinciden.");
    setSubmitting(true);
    setError("");
    try {
      const { data: registration, error: registrationError } =
        await supabase.functions.invoke("kiosk-register", {
          body: {
            email: form.email.trim().toLowerCase(),
            password: form.password,
            nombre: form.nombre.trim(),
            rut: normalizeRut(form.rut),
            telefono: phone(form.telefono),
            fecha_nacimiento: form.fechaNacimiento,
            gimnasio_id: catalog.gimnasio.id,
            sucursal_id: branchId,
            plan_id: selectedPlan.id,
            permanencia_meses: permanenciaMeses,
            responsable_pago: form.mismoResponsable
              ? { misma_persona: true }
              : {
                  misma_persona: false,
                  nombre: form.responsableNombre.trim(),
                  rut: normalizeRut(form.responsableRut),
                  email: form.responsableEmail.trim().toLowerCase(),
                  telefono: phone(form.responsableTelefono),
                },
            declaracion_salud: {
              estado: form.saludEstado,
              detalle: form.saludDetalle.trim(),
              restricciones: form.saludRestricciones.trim(),
            },
            contacto_emergencia: {
              nombre: form.emergenciaNombre.trim(),
              telefono: form.emergenciaTelefono.trim()
                ? phone(form.emergenciaTelefono)
                : "",
            },
            firma_nombre: form.firmaNombre.trim(),
            firma_rut: normalizeRut(form.firmaRut),
            aceptaciones: form.aceptaciones,
            documentos_aceptados: catalog.documentos
              .filter((d) => form.documentos[d.id])
              .map((d) => ({ id: d.id, version: d.version })),
          },
        });
      if (registrationError) throw new Error("registration failed");
      if (!registration?.ok) {
        const code = String(registration?.error || "");
        if (code === "ACCOUNT_EXISTS") {
          const existingLogin = await supabase.auth.signInWithPassword({
            email: form.email.trim().toLowerCase(),
            password: form.password,
          });
          if (existingLogin.error) throw existingLogin.error;
          sessionStorage.setItem("kiosco_plan_id", selectedPlan.id);
          sessionStorage.setItem(
            "kiosco_permanencia_meses",
            String(permanenciaMeses),
          );
          sessionStorage.setItem("kiosco_return", window.location.pathname);
          setDone(true);
          return;
        }
        if (code === "ACCOUNT_OTHER_CONTEXT")
          throw new Error("account other context");
        if (code === "RATE_LIMITED") throw new Error("rate limit");
        if (code === "DOCUMENT_VERSION_CHANGED")
          throw new Error(
            "Los documentos fueron actualizados. Recarga la página y revísalos nuevamente.",
          );
        if (
          code === "INVALID_CONTRACT_DATA" ||
          code === "INVALID_PAYER_DATA" ||
          code === "INVALID_HEALTH_DATA"
        )
          throw new Error("Revisa los datos y aceptaciones del contrato.");
        throw new Error("registration failed");
      }
      const login = await supabase.auth.signInWithPassword({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      if (login.error) throw login.error;
      sessionStorage.setItem("kiosco_plan_id", selectedPlan.id);
      sessionStorage.setItem(
        "kiosco_permanencia_meses",
        String(permanenciaMeses),
      );
      sessionStorage.setItem("kiosco_return", window.location.pathname);
      setError("");
      setDone(true);
    } catch (e) {
      setForm((prev) => ({ ...prev, password: "", confirmacion: "" }));
      const message = e instanceof Error ? e.message : "";
      setError(
        message === "account other context"
          ? "Este correo ya está asociado a una cuenta existente. Usa otro correo o solicita ayuda en recepción para recuperar el acceso."
          : publicError(e),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <Shell>
        <div className="text-center py-24">
          <i className="ri-loader-4-line animate-spin text-4xl" />
          <p className="mt-4">Preparando autoinscripción…</p>
        </div>
      </Shell>
    );
  if (!gimnasioSlug)
    return (
      <Shell>
        <div className="text-center py-24">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </div>
      </Shell>
    );
  if (!catalog)
    return (
      <Shell>
        <Alert text={error || "Gimnasio no disponible."} />
        <button
          onClick={() => navigate("/inscripcion")}
          className="mt-5 px-5 py-3 bg-gray-900 text-white rounded-xl"
        >
          Volver
        </button>
      </Shell>
    );
  if (done)
    return (
      <Shell>
        <div className="max-w-xl mx-auto bg-white border rounded-3xl p-8 text-center shadow-sm">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
            <i
              className={`${facialComplete ? "ri-user-star-line" : paymentComplete ? "ri-checkbox-circle-line" : "ri-user-follow-line"} text-4xl`}
            />
          </div>
          <h1 className="text-3xl font-bold mt-5">
            {facialComplete
              ? "Registro completado"
              : paymentComplete
                ? "Pago aprobado"
                : "Cuenta creada correctamente"}
          </h1>
          <p className="text-gray-600 mt-3">
            {facialComplete ? (
              <>
                Tu plan está activo y tu rostro quedó registrado para el acceso
                a <b>{catalog.gimnasio.nombre}</b>.
              </>
            ) : paymentComplete ? (
              <>
                Tu plan de <b>{catalog.gimnasio.nombre}</b> ya está activo. Solo
                falta registrar tu rostro para el acceso.
              </>
            ) : (
              <>
                Tu registro quedó asociado a <b>{catalog.gimnasio.nombre}</b>.
                Elige cómo pagar para activar tu plan.
              </>
            )}
          </p>
          {reconciling && (
            <div className="mt-6 text-gray-600">
              <i className="ri-loader-4-line animate-spin mr-2" />
              Confirmando pago y activando plan…
            </div>
          )}
          {paymentError && (
            <div className="mt-5">
              <Alert text={paymentError} />
            </div>
          )}
          {paymentComplete && !facialComplete && (
            <div className="mt-6">
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-left">
                <p className="font-semibold text-blue-900">
                  Paso final: captura facial
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  Mira de frente, sin lentes oscuros y con buena iluminación.
                </p>
              </div>
              {cameraOpen ? (
                <>
                  <div className="relative mt-5 mx-auto max-w-sm overflow-hidden rounded-2xl bg-black aspect-[3/4]">
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      className="w-full h-full object-cover scale-x-[-1]"
                    />
                  </div>
                  <label className="flex gap-3 text-left mt-4 p-4 rounded-xl bg-gray-50">
                    <input
                      type="checkbox"
                      checked={facialConsent}
                      onChange={(e) => setFacialConsent(e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-sm">
                      Autorizo el tratamiento de mi imagen facial exclusivamente
                      para identificarme y controlar mi acceso al gimnasio.
                    </span>
                  </label>
                  <button
                    disabled={facialSaving || !facialConsent}
                    onClick={() => void captureFace()}
                    className="w-full mt-4 px-6 py-4 rounded-xl text-white font-bold disabled:opacity-50"
                    style={{ backgroundColor: accent }}
                  >
                    {facialSaving
                      ? "Guardando rostro…"
                      : "Capturar y finalizar"}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => void openCamera()}
                  className="w-full mt-5 px-6 py-4 rounded-xl text-white font-bold"
                  style={{ backgroundColor: accent }}
                >
                  <i className="ri-camera-line mr-2" />
                  Abrir cámara
                </button>
              )}
            </div>
          )}
          {fintocPending && !reconciling && (
            <button
              onClick={() => void reconcileFintoc()}
              className="w-full mt-4 py-3 rounded-xl border font-bold"
            >
              Comprobar pago Fintoc
            </button>
          )}
          {flowPending && !reconciling && (
            <button onClick={() => void reconcileFlow()} className="w-full mt-4 py-3 rounded-xl border font-bold">Actualizar estado de Flow</button>
          )}
          {!paymentComplete && !reconciling && !fintocPending && !flowPending && (
            <>
              <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-left">
                <p className="font-semibold text-emerald-900">
                  Siguiente paso: pagar el plan
                </p>
                <p className="text-sm text-emerald-800 mt-1">
                  Continuarás en el sitio seguro del medio de pago.{" "}
                  {catalog.gimnasio.nombre} no almacenará los datos de tu
                  tarjeta.
                </p>
              </div>
              {returnedPreapproval ? (
                <button
                  onClick={() => void reconcilePayment()}
                  className="w-full mt-6 px-6 py-4 rounded-xl text-white font-bold"
                  style={{ backgroundColor: accent }}
                >
                  Volver a confirmar el pago
                </button>
              ) : (
                <div className="mt-6 space-y-3">
                  {selectedPlan?.permitir_webpay_plus_kiosco && webpayEnabledForBranch && (
                    <button
                      disabled={paymentStarting}
                      onClick={() => void startWebpay()}
                      className="w-full px-6 py-4 rounded-xl text-white font-bold disabled:opacity-60"
                      style={{ backgroundColor: accent }}
                    >
                      {paymentStarting
                        ? "Abriendo Transbank…"
                        : "Pagar con Webpay Plus"}
                    </button>
                  )}
                  {activeRecurringProviders(selectedPlan).length > 0 && (
                    <div className="space-y-3">
                      {activeRecurringProviders(selectedPlan).map((provider) => <div key={provider} className="rounded-2xl border p-3 space-y-2">
                        <button
                          disabled={paymentStarting}
                          onClick={() => void startRecurringPayment(provider, false)}
                          className="w-full px-6 py-4 rounded-xl border-2 font-bold disabled:opacity-60"
                          style={{ borderColor: accent, color: accent }}
                        >
                          {paymentStarting && selectedPaymentProvider === provider
                            ? "Abriendo medio de pago…"
                            : `Activar mensualidad automática con ${recurringProviderName(provider)}`}
                        </button>
                        {["mercadopago","flow"].includes(provider) && <button
                          disabled={paymentStarting}
                          onClick={() => void startRecurringPayment(provider, true)}
                          className="w-full px-5 py-3 rounded-xl bg-slate-900 text-white font-bold disabled:opacity-60"
                        ><i className="ri-qr-code-line mr-2"/>Continuar con {recurringProviderName(provider)} desde mi teléfono</button>}
                      </div>)}
                    </div>
                  )}
                  {!webpayEnabledForBranch && activeRecurringProviders(selectedPlan).length === 0 && (
                    <Alert text="Este plan no tiene medios de pago activos en la sucursal seleccionada. Solicita ayuda en recepción." />
                  )}
                </div>
              )}
            </>
          )}
          {facialComplete && (
            <button
              onClick={() => void reset()}
              className="mt-6 px-6 py-3 rounded-xl border font-semibold"
            >
              Finalizar y limpiar
            </button>
          )}
        </div>
        {paymentQrUrl && <div className="fixed inset-0 z-[150] bg-black/60 p-4 grid place-items-center"><section className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-xl">
          <header className="p-5 border-b flex justify-between text-left"><div><h2 className="text-xl font-bold">Continúa desde tu teléfono</h2><p className="text-sm text-gray-500 mt-1">Escanea el código para abrir el pago.</p></div><button onClick={() => { setPaymentQrUrl(""); setPaymentQr(""); }} className="text-2xl"><i className="ri-close-line"/></button></header>
          <div className="p-6 text-center"><div className="max-w-xs mx-auto border rounded-2xl p-3">{paymentQr && <img src={paymentQr} alt="QR para continuar la inscripción" className="w-full"/>}</div><p className="text-sm text-gray-500 mt-4">Puedes volver a abrir este mismo enlace durante 30 minutos. Esta tablet ya puede iniciar otra inscripción.</p><button onClick={() => void reset()} className="mt-5 w-full px-5 py-3 rounded-xl text-white font-bold" style={{ backgroundColor: accent }}>Nueva inscripción</button></div>
        </section></div>}
      </Shell>
    );
  return (
    <Shell>
      <div className="flex items-center gap-4 mb-7">
        {catalog.gimnasio.logo_url && (
          <img
            src={catalog.gimnasio.logo_url}
            className="w-16 h-16 object-contain rounded-xl bg-white p-2"
          />
        )}
        <Header
          title={catalog.gimnasio.nombre}
          subtitle="Inscripción y mensualidad automática"
        />
      </div>
      {error && <Alert text={error} onClose={() => setError("")} />}{" "}
      {!selectedPlan ? (
        <>
          <label className="block font-semibold mb-2">Sucursal</label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="w-full bg-white border rounded-xl p-4 text-lg mb-7"
          >
            <option value="">Selecciona una sucursal</option>
            {catalog.sucursales.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre}
                {s.direccion ? ` — ${s.direccion}` : ""}
              </option>
            ))}
          </select>
          <h2 className="text-2xl font-bold mb-4">Elige tu plan</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {catalog.planes.map((p) => (
              <button
                key={p.id}
                disabled={!branchId}
                onClick={() => {
                  setSelectedPlan(p);
                  setSelectedPaymentProvider(recurringProviders(p)[0] || "");
                  setPermanenciaMeses(p.permanencia_meses === 6 ? 6 : 12);
                }}
                className="text-left bg-white border-2 border-transparent hover:border-teal-500 disabled:opacity-40 rounded-2xl p-6 shadow-sm"
              >
                <p className="text-xl font-bold">{p.nombre}</p>
                <p className="text-gray-500 mt-2 min-h-10">{p.descripcion}</p>
                <p
                  className="text-4xl font-extrabold mt-6"
                  style={{ color: accent }}
                >
                  {money(p.precio)}
                </p>
                <p className="text-sm text-gray-500">
                  por mes · cobro automático
                </p>
                <ul className="mt-5 space-y-2 text-sm">
                  {(p.beneficios || []).slice(0, 4).map((b, i) => (
                    <li key={i}>
                      <i className="ri-check-line text-emerald-600 mr-2" />
                      {b}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 font-semibold">
                  Seleccionar <i className="ri-arrow-right-line" />
                </p>
              </button>
            ))}
          </div>
          {catalog.planes.length === 0 && (
            <div className="bg-white border border-dashed rounded-2xl p-10 text-center text-gray-500">
              No hay planes recurrentes habilitados para autoinscripción.
            </div>
          )}
        </>
      ) : (
        <form
          onSubmit={submit}
          className="max-w-4xl mx-auto bg-white rounded-3xl border p-6 md:p-8 shadow-sm space-y-8"
        >
          <button
            type="button"
            onClick={() => setSelectedPlan(null)}
            className="text-sm text-gray-600"
          >
            <i className="ri-arrow-left-line" /> Cambiar plan
          </button>
          <section className="rounded-2xl p-5 bg-gray-50">
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <p className="font-bold text-lg">{selectedPlan.nombre}</p>
                <p className="text-sm text-gray-600">
                  Cobro automático mensual. Podrás elegir entre{" "}
                  {recurringProviders(selectedPlan).map(recurringProviderName).join(" o ")}
                </p>
                <p className="text-sm text-gray-600">
                  Matrícula: {money(selectedPlan.matricula || 0)}
                </p>
              </div>
              <b className="text-xl" style={{ color: accent }}>
                {money(selectedPlan.precio)} / mes
              </b>
            </div>
            <fieldset className="mt-5">
              <legend className="font-semibold mb-2">Permanencia mínima</legend>
              <div className="grid grid-cols-2 gap-3">
                {([6, 12] as const).map((months) => (
                  <label
                    key={months}
                    className="cursor-pointer rounded-xl border-2 px-4 py-3 flex items-center gap-3 bg-white"
                    style={{
                      borderColor:
                        permanenciaMeses === months ? accent : undefined,
                    }}
                  >
                    <input
                      type="radio"
                      name="permanencia"
                      value={months}
                      checked={permanenciaMeses === months}
                      onChange={() => setPermanenciaMeses(months)}
                    />
                    <span>
                      <strong className="block">{months} meses</strong>
                      <span className="text-xs text-gray-500">
                        Cobro recurrente mensual
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </section>

          <FormSection title="1. Datos del socio">
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Nombre completo"
                minLength={3}
                value={form.nombre}
                onChange={(v) =>
                  setForm({
                    ...form,
                    nombre: v,
                    firmaNombre: v,
                    datosFirmaConfirmados: false,
                  })
                }
              />
              <Field
                label="RUT"
                placeholder="12.345.678-5"
                value={form.rut}
                onChange={(v) =>
                  setForm({
                    ...form,
                    rut: v,
                    firmaRut: v,
                    datosFirmaConfirmados: false,
                  })
                }
              />
              <Field
                label="Correo electrónico"
                type="email"
                value={form.email}
                onChange={(v) =>
                  setForm({ ...form, email: v, datosFirmaConfirmados: false })
                }
              />
              <Field
                label="Teléfono"
                type="tel"
                inputMode="tel"
                placeholder="9 1234 5678"
                value={form.telefono}
                onChange={(v) =>
                  setForm({
                    ...form,
                    telefono: v,
                    datosFirmaConfirmados: false,
                  })
                }
              />
              <Field
                label="Fecha de nacimiento"
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={form.fechaNacimiento}
                onChange={(v) => setForm({ ...form, fechaNacimiento: v })}
              />
            </div>
          </FormSection>

          <FormSection title="2. Responsable de pago">
            <Check
              checked={form.mismoResponsable}
              onChange={(checked) =>
                setForm({ ...form, mismoResponsable: checked })
              }
              text="El socio también es el responsable de pago"
            />
            {!form.mismoResponsable && (
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field
                  label="Nombre del responsable"
                  value={form.responsableNombre}
                  onChange={(v) => setForm({ ...form, responsableNombre: v })}
                />
                <Field
                  label="RUT del responsable"
                  value={form.responsableRut}
                  onChange={(v) => setForm({ ...form, responsableRut: v })}
                />
                <Field
                  label="Correo del responsable"
                  type="email"
                  value={form.responsableEmail}
                  onChange={(v) => setForm({ ...form, responsableEmail: v })}
                />
                <Field
                  label="Teléfono del responsable"
                  type="tel"
                  value={form.responsableTelefono}
                  onChange={(v) => setForm({ ...form, responsableTelefono: v })}
                />
              </div>
            )}
          </FormSection>

          <FormSection title="3. Salud y seguridad">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium mb-2">
                  Declaración de salud
                </span>
                <select
                  className="w-full border rounded-xl px-4 py-3"
                  value={form.saludEstado}
                  onChange={(e) =>
                    setForm({ ...form, saludEstado: e.target.value })
                  }
                >
                  <option value="sin_condicion">
                    No conozco una condición que me impida entrenar
                  </option>
                  <option value="con_condicion">
                    Tengo una condición, lesión, cirugía o restricción
                  </option>
                </select>
              </label>
              {form.saludEstado === "con_condicion" && (
                <>
                  <TextArea
                    label="Condición o antecedente relevante"
                    value={form.saludDetalle}
                    onChange={(v) => setForm({ ...form, saludDetalle: v })}
                  />
                  <TextArea
                    label="Restricciones o precauciones profesionales"
                    value={form.saludRestricciones}
                    onChange={(v) =>
                      setForm({ ...form, saludRestricciones: v })
                    }
                  />
                </>
              )}
              <Field
                required={false}
                label="Contacto de emergencia (opcional)"
                value={form.emergenciaNombre}
                onChange={(v) => setForm({ ...form, emergenciaNombre: v })}
              />
              <Field
                required={false}
                label="Teléfono de emergencia"
                type="tel"
                value={form.emergenciaTelefono}
                onChange={(v) => setForm({ ...form, emergenciaTelefono: v })}
              />
            </div>
          </FormSection>

          <FormSection title="4. Revisa los documentos">
            <p className="text-sm text-gray-600 mb-4">
              Puedes abrir cada documento para revisarlo. Guardaremos la versión
              exacta aceptada junto con tu expediente.
            </p>
            <label
              className="mb-4 flex items-center gap-3 rounded-xl border-2 p-4 cursor-pointer"
              style={{
                borderColor: allDocumentsAccepted ? accent : undefined,
                backgroundColor: allDocumentsAccepted
                  ? `${accent}0D`
                  : undefined,
              }}
            >
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={allDocumentsAccepted}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    documentos: Object.fromEntries(
                      catalog.documentos.map((document) => [
                        document.id,
                        checked,
                      ]),
                    ),
                  }));
                }}
              />
              <span>
                <strong className="block">Aceptar todos</strong>
                <span className="text-xs text-gray-500">
                  Acepto el contrato y todos sus anexos vigentes.
                </span>
              </span>
            </label>
            <div className="space-y-3">
              {catalog.documentos.map((document) => (
                <div
                  key={document.id}
                  className="border rounded-xl p-4 flex items-start gap-3"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={!!form.documentos[document.id]}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        documentos: {
                          ...form.documentos,
                          [document.id]: e.target.checked,
                        },
                      })
                    }
                  />
                  <div className="flex-1">
                    <p className="font-semibold">{document.titulo}</p>
                    <p className="text-xs text-gray-500">
                      Versión {document.version}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenDocument(document)}
                    className="px-3 py-2 rounded-lg border text-sm font-semibold"
                  >
                    Leer
                  </button>
                </div>
              ))}
            </div>
          </FormSection>

          <FormSection title="5. Aceptaciones específicas">
            <label
              className="mb-4 flex items-center gap-3 rounded-xl border-2 p-4 cursor-pointer"
              style={{
                borderColor: allSpecificAcceptances ? accent : undefined,
                backgroundColor: allSpecificAcceptances
                  ? `${accent}0D`
                  : undefined,
              }}
            >
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={allSpecificAcceptances}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm((prev) => ({
                    ...prev,
                    aceptaciones: Object.fromEntries(
                      Object.keys(prev.aceptaciones).map((key) => [
                        key,
                        checked,
                      ]),
                    ),
                  }));
                }}
              />
              <span>
                <strong className="block">Aceptar todos</strong>
                <span className="text-xs text-gray-500">
                  Confirmo en una sola acción todas las declaraciones
                  siguientes.
                </span>
              </span>
            </label>
            <div className="space-y-3">
              <Check
                checked={form.aceptaciones.contrato}
                onChange={(v) =>
                  setForm({
                    ...form,
                    aceptaciones: { ...form.aceptaciones, contrato: v },
                  })
                }
                text="Leí y acepto el contrato de membresía."
              />
              <Check
                checked={form.aceptaciones.reglamento}
                onChange={(v) =>
                  setForm({
                    ...form,
                    aceptaciones: { ...form.aceptaciones, reglamento: v },
                  })
                }
                text="Leí y acepto el reglamento de uso y convivencia."
              />
              <Check
                checked={form.aceptaciones.condiciones}
                onChange={(v) =>
                  setForm({
                    ...form,
                    aceptaciones: { ...form.aceptaciones, condiciones: v },
                  })
                }
                text={`Comprendo el precio, la matrícula, la permanencia de ${permanenciaMeses} meses y las condiciones de término.`}
              />
              <Check
                checked={form.aceptaciones.salud_datos}
                onChange={(v) =>
                  setForm({
                    ...form,
                    aceptaciones: { ...form.aceptaciones, salud_datos: v },
                  })
                }
                text="Leí el anexo de salud, seguridad y tratamiento de datos."
              />
              <Check
                checked={form.aceptaciones.pago_recurrente}
                onChange={(v) =>
                  setForm({
                    ...form,
                    aceptaciones: { ...form.aceptaciones, pago_recurrente: v },
                  })
                }
                text={`Autorizo el cobro recurrente mensual de ${money(selectedPlan.precio)} durante una permanencia mínima de ${permanenciaMeses} meses.`}
              />
              <Check
                checked={form.aceptaciones.retiro_anticipado}
                onChange={(v) =>
                  setForm({
                    ...form,
                    aceptaciones: {
                      ...form.aceptaciones,
                      retiro_anticipado: v,
                    },
                  })
                }
                text="Comprendo el cargo compensatorio por retiro anticipado y sus excepciones."
              />
              <Check
                checked={form.aceptaciones.veracidad_salud}
                onChange={(v) =>
                  setForm({
                    ...form,
                    aceptaciones: { ...form.aceptaciones, veracidad_salud: v },
                  })
                }
                text="Declaro que la información personal y de salud entregada es correcta."
              />
            </div>
          </FormSection>

          <FormSection title="6. Firma electrónica y acceso">
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Nombre completo para la firma"
                value={form.firmaNombre}
                onChange={(v) =>
                  setForm({
                    ...form,
                    firmaNombre: v,
                    datosFirmaConfirmados: false,
                  })
                }
              />
              <Field
                label="RUT para la firma"
                value={form.firmaRut}
                onChange={(v) =>
                  setForm({
                    ...form,
                    firmaRut: v,
                    datosFirmaConfirmados: false,
                  })
                }
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Estos campos se completan automáticamente con los datos ingresados
              al inicio.
            </p>
            <label className="flex gap-3 items-start cursor-pointer rounded-xl border p-4 my-4">
              <input
                type="checkbox"
                checked={form.datosFirmaConfirmados}
                onChange={(e) =>
                  setForm({ ...form, datosFirmaConfirmados: e.target.checked })
                }
                className="mt-1 w-5 h-5"
              />
              <span className="text-sm">
                <strong className="block">
                  Confirmo que mis datos son correctos
                </strong>
                Autorizo que estos datos se utilicen en el contrato, sus anexos
                y mi firma electrónica.
              </span>
            </label>
            <div className="grid md:grid-cols-2 gap-4">
              <Field
                label="Contraseña"
                type="password"
                minLength={8}
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
              />
              <Field
                label="Repetir contraseña"
                type="password"
                minLength={8}
                value={form.confirmacion}
                onChange={(v) => setForm({ ...form, confirmacion: v })}
              />
            </div>
            <p className="text-xs text-gray-500 mt-3">
              La aceptación quedará registrada con fecha, versión de los
              documentos y evidencia técnica. El consentimiento biométrico se
              solicitará por separado.
            </p>
          </FormSection>
          <button
            disabled={submitting}
            className="w-full py-4 text-white text-lg font-bold rounded-xl disabled:opacity-60"
            style={{ backgroundColor: accent }}
          >
            {submitting
              ? "Creando expediente…"
              : "Firmar y continuar al registro de tarjeta"}
          </button>
        </form>
      )}
      {openDocument && (
        <DocumentModal
          document={openDocument}
          onClose={() => setOpenDocument(null)}
          preview={{
            nombre: form.nombre,
            rut: form.rut,
            fechaNacimiento: form.fechaNacimiento,
            email: form.email,
            telefono: form.telefono,
            responsable: form.mismoResponsable
              ? {
                  nombre: form.nombre,
                  rut: form.rut,
                  email: form.email,
                  telefono: form.telefono,
                }
              : {
                  nombre: form.responsableNombre,
                  rut: form.responsableRut,
                  email: form.responsableEmail,
                  telefono: form.responsableTelefono,
                },
            plan: { ...selectedPlan, permanencia_meses: permanenciaMeses },
            sucursal:
              catalog.sucursales.find((s) => s.id === branchId)?.nombre || "",
            saludEstado: form.saludEstado,
            saludDetalle: form.saludDetalle,
            saludRestricciones: form.saludRestricciones,
            emergenciaNombre: form.emergenciaNombre,
            emergenciaTelefono: form.emergenciaTelefono,
          }}
        />
      )}
      <p className="text-center text-xs text-gray-500 mt-8">
        Al finalizar, esta tablet eliminará los datos ingresados.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-[#f5f4f1] text-slate-900 p-5 md:p-10">
      <div className="max-w-6xl mx-auto">{children}</div>
    </main>
  );
}
function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-extrabold">{title}</h1>
      <p className="text-gray-600 mt-1">{subtitle}</p>
    </div>
  );
}
function Alert({ text, onClose }: { text: string; onClose?: () => void }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="mb-5 p-4 rounded-xl border border-red-200 bg-red-50 text-red-700 flex items-start gap-3"
    >
      <i className="ri-error-warning-line mt-0.5" />
      <span className="flex-1">{text}</span>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Cerrar mensaje">
          <i className="ri-close-line" />
        </button>
      )}
    </div>
  );
}
function FormSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t pt-7">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      {children}
    </section>
  );
}
function Check({
  checked,
  onChange,
  text,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  text: string;
}) {
  return (
    <label className="flex gap-3 items-start cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <span className="text-sm">{text}</span>
    </label>
  );
}
function TextArea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-2">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full border rounded-xl px-4 py-3"
      />
    </label>
  );
}
type ContractPreview = {
  nombre: string;
  rut: string;
  fechaNacimiento: string;
  email: string;
  telefono: string;
  responsable: { nombre: string; rut: string; email: string; telefono: string };
  plan: Plan;
  sucursal: string;
  saludEstado: string;
  saludDetalle: string;
  saludRestricciones: string;
  emergenciaNombre: string;
  emergenciaTelefono: string;
};
const displayValue = (
  value: string | number | null | undefined,
  fallback = "Pendiente de completar",
) => String(value ?? "").trim() || fallback;
const localDate = (value: string) =>
  value
    ? new Intl.DateTimeFormat("es-CL", { timeZone: "UTC" }).format(
        new Date(`${value}T12:00:00Z`),
      )
    : "Pendiente de completar";
function completedDocument(document: ContractDocument, data: ContractPreview) {
  const payer = data.responsable;
  const planBlock = `DATOS DEL CONTRATO\nSocio / Alumno: ${displayValue(data.nombre)}\nRUT: ${displayValue(data.rut)}\nFecha de nacimiento: ${localDate(data.fechaNacimiento)}\nCorreo: ${displayValue(data.email)}\nTeléfono / WhatsApp: ${displayValue(data.telefono)}\nTitular / Responsable de pago: ${displayValue(payer.nombre)}\nRUT responsable: ${displayValue(payer.rut)}\nCorreo responsable: ${displayValue(payer.email)}\nTeléfono responsable: ${displayValue(payer.telefono)}\nPlan: ${data.plan.nombre}\nModalidad: Recurrente ${data.plan.permanencia_meses || 0} meses\nValor mensual: ${money(data.plan.precio)}\nMatrícula: ${money(data.plan.matricula || 0)}\nSucursal: ${displayValue(data.sucursal)}\nDía ordinario de cobro: fecha de contratación`;
  const signature = `ACEPTACIÓN ELECTRÓNICA\nSocio / titular: ${displayValue(data.nombre)}\nRUT: ${displayValue(data.rut)}\nFirma electrónica: se completará al confirmar el formulario`;
  let text = document.contenido
    .replace(/^MOWE\s*/, "")
    .replace(/^CONTRATO DE INCORPORACIÓN Y MEMBRESÍA\s*/, "");
  if (document.codigo === "contrato_membresia")
    text = text
      .replace(/Socio \/ Alumno[\s\S]*?(?=PRIMERO\.)/, `${planBlock}\n\n`)
      .replace(
        /SOCIEDAD COMERCIAL LAETIT SpA\nRUT 78\.331\.569-6[\s\S]*$/,
        `SOCIEDAD COMERCIAL LAETIT SpA · RUT 78.331.569-6\n${signature}`,
      );
  if (document.codigo === "anexo_1_reglamento")
    text = text.replace(/Nombre socio \/ titular:[\s\S]*$/, signature);
  if (document.codigo === "anexo_2_condiciones")
    text = text
      .replace(
        /1\. Ficha del plan\.[\s\S]*?(?=2\. Pago recurrente\.)/,
        `1. Ficha del plan.\n${planBlock}\n\n`,
      )
      .replace(/Nombre socio \/ titular:[\s\S]*$/, signature);
  if (document.codigo === "anexo_3_salud_datos") {
    const health =
      data.saludEstado === "con_condicion"
        ? `☐ Sin condición conocida\n☒ Condición, restricción, lesión o cirugía: ${displayValue(data.saludDetalle)}\nRestricciones o precauciones profesionales: ${displayValue(data.saludRestricciones, "No informadas")}`
        : `☒ Declaro no conocer una condición que me impida realizar actividad física conforme al plan contratado.\n☐ Condición, restricción, lesión o cirugía relevante`;
    text = text
      .replace(
        /1\. Declaración de salud\.[\s\S]*?(?=2\. Deber de informar\.)/,
        `1. Declaración de salud.\n${health}\nContacto de emergencia: ${displayValue(data.emergenciaNombre, "No informado")} · Tel.: ${displayValue(data.emergenciaTelefono, "No informado")}\n\n`,
      )
      .replace(/Nombre socio \/ titular:[\s\S]*$/, signature);
  }
  return text
    .split("\n")
    .map((line) => line.replace(/^\s*\|(?:\s*\|)*\s*$/, "").trim())
    .filter(Boolean);
}
function DocumentModal({
  document,
  onClose,
  preview,
}: {
  document: ContractDocument;
  onClose: () => void;
  preview: ContractPreview;
}) {
  const lines = completedDocument(document, preview);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 p-3 md:p-8 grid place-items-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col">
        <div className="p-5 border-b flex justify-between gap-4">
          <div>
            <h2 className="font-bold text-xl">{document.titulo}</h2>
            <p className="text-xs text-gray-500">
              Versión {document.version} · Vista completada con tus datos
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            <i className="ri-close-line text-2xl" />
          </button>
        </div>
        <article className="p-5 md:p-8 overflow-y-auto text-sm leading-7 space-y-3">
          {lines.map((line, index) => {
            const heading =
              /^(DATOS DEL CONTRATO|ACEPTACIÓN ELECTRÓNICA|ANEXO \d|[A-ZÁÉÍÓÚÑ]+\.|\d+\.)/.test(
                line,
              );
            const datum =
              /^(Socio|RUT|Fecha de nacimiento|Correo|Teléfono|Titular|Plan|Modalidad|Valor mensual|Matrícula|Sucursal|Día ordinario|Firma electrónica|Contacto de emergencia|Restricciones)/.test(
                line,
              );
            return heading ? (
              <h3
                key={index}
                className="font-bold text-base text-slate-900 pt-3"
              >
                {line}
              </h3>
            ) : datum ? (
              <div
                key={index}
                className="grid sm:grid-cols-[210px_1fr] gap-1 border-b border-slate-100 py-1"
              >
                <span className="font-medium">{line.split(":")[0]}</span>
                <span>{line.slice(line.indexOf(":") + 1).trim()}</span>
              </div>
            ) : (
              <p key={index} className="text-slate-700">
                {line}
              </p>
            );
          })}
        </article>
        <div className="p-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-slate-900 text-white font-semibold"
          >
            Cerrar documento
          </button>
        </div>
      </div>
    </div>
  );
}
type FieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  pattern?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  minLength?: number;
  max?: string;
  required?: boolean;
};
function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  pattern,
  inputMode,
  minLength,
  max,
  required = true,
}: FieldProps) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-2">{label}</span>
      <input
        required={required}
        type={type}
        value={value}
        placeholder={placeholder}
        pattern={pattern}
        inputMode={inputMode}
        minLength={minLength}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border rounded-xl px-4 py-3 text-base invalid:border-red-300"
      />
    </label>
  );
}
